import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  IProject,
  IProjectDetail,
  ISupportLanguage,
} from 'utils/types/storyTypes';
import { theme } from 'utils/style/theme';
import { Select, message } from 'antd';
import DefaultInput from 'components/input/DefaultInput';
import DefaultTextArea from 'components/input/DefaultTextArea';
import UploadStoryIcon from './UploadStoryIcon';
import SubmitFlexBox from 'components/container/SubmitFlexBox';
import SubmitButton from 'components/button/SubmitButton';
import PrimaryButton from 'components/button/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { storyApi } from 'apis/story/storyApis';
import SelectStyled from 'components/SelectStyled';
import SelectBox from 'components/SelectBox';

/**
 * * 오토메 - 작품 정보 수정
 */

interface IProps {
  langList: ISupportLanguage[] | null;
  data: IProjectDetail;
  listData: IProject | null;
}

const OtomeProjectContent: React.FC<IProps> = ({
  langList,
  data,
  listData,
}) => {
  const [lang, setLang] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [summary, setSummary] = useState<string | null>(null);
  const [writer, setWriter] = useState<string | null>(null);
  const [original, setOriginal] = useState<string | null>(null);
  const [translator, setTranslator] = useState<string | null>(null);
  const [iconImage, setIconImage] = useState<string | null>('');
  const [iconKey, setIconKey] = useState<string | null>('');
  const [iconBucket, setIconBucket] = useState<string | null>('');

  const { t } = useTranslation();
  const [isCancelButtonDisabled, setCancelButtonDisabled] = useState(true);

  const setValueForm = () => {
    if (data) {
      setLang(data.lang);
      setTitle(data.title);
      setSummary(data.summary);
      setWriter(data.writer);
      setOriginal(data.original);
      setTranslator(data.translator);
      setIconImage(data.icon_url);
    }
  };

  useEffect(() => {
    setValueForm();
  }, [data]);

  const handleInputChange = (e, type) => {
    switch (type) {
      case 'title':
        setTitle(e.target.value);
        checkFieldHasChange(`${e.target.value}`, data?.title);
        break;
      case 'summary':
        setSummary(e.target.value);
        checkFieldHasChange(`${e.target.value}`, data?.summary);
        break;
      case 'writer':
        setWriter(e.target.value);
        checkFieldHasChange(`${e.target.value}`, data?.writer);
        break;
      case 'original':
        setOriginal(e.target.value);
        checkFieldHasChange(`${e.target.value}`, data?.original);
        break;
      case 'translator':
        setTranslator(e.target.value);
        checkFieldHasChange(`${e.target.value}`, data?.translator);
        break;
      default:
        setTitle(e.target.value);
        checkFieldHasChange(`${e.target.value}`, data?.title);
        break;
    }
  };

  const handleCancel = () => {
    if (!isCancelButtonDisabled) {
      setValueForm();
      setCancelButtonDisabled(true);
    }
  };

  const checkFieldHasChange = (
    newValue: string | null,
    oldValue: string | null,
  ) => {
    if (newValue == oldValue) {
      setCancelButtonDisabled(true);
      return;
    }
    setCancelButtonDisabled(false);
  };

  const handleSubmit = async () => {
    if (listData && !isCancelButtonDisabled) {
      const hasExistLanguage = listData.projectDetails.some((item) => {
        // Exclude the current element (data.id) from the comparison when updating
        return item.lang === lang && item.id !== data.id;
      });
      const itemModifyStoryDetail: IProjectDetail = {
        lang,
        title,
        summary,
        writer,
        icon_url: iconImage,
        icon_key: iconKey,
        icon_bucket: iconBucket,
        original,
        translator,
      };
      if (!hasExistLanguage) {
        if (data.id) {
          //update
          const valuesForm = {
            ...listData,
            projectDetails: listData?.projectDetails.map((ele) => {
              if (ele.id === data?.id) {
                return {
                  id: data.id,
                  ...itemModifyStoryDetail,
                };
              }
              return ele;
            }),
          };
          await storyApi.updateDetailStory(listData.project_id, valuesForm);
        } else {
          //create new
          const valuesForm = {
            ...listData,
            projectDetails: [...listData.projectDetails, itemModifyStoryDetail],
          };

          await storyApi.updateDetailStory(listData.project_id, valuesForm);
        }
      } else {
        message.error('Detail story has exist language', 1);
      }
    }
  };
  return (
    <OtomeProjectContentWrapper>
      <SectionWarpper>
        <p className='title'>{t('updateStory.form.language')}</p>
        <SelectBox
          value={data?.lang}
          onChange={(e) => {
            setLang(`${e}`);
            checkFieldHasChange(`${e}`, data?.lang);
          }}
          options={langList?.map((v, i) => {
            return {
              value: v.code,
              label: v.code_name,
            };
          })}
        />
      </SectionWarpper>
      <SectionWarpper>
        <p className='title'>{t('updateStory.form.title')}</p>
        <DefaultInput
          title=''
          name='title'
          value={title}
          onChange={(e) => {
            handleInputChange(e, 'title');
          }}
          allowClear={true}
          noMarginBottom
          maxLength={30}
        />
        <p className='length-subtext'>
          {typeof title?.length === 'number'
            ? `${title?.length}/30 characters`
            : '0/30 characters'}
        </p>
      </SectionWarpper>
      <SectionWarpper>
        <p className='title'>{t('updateStory.form.summary')}</p>
        <DefaultTextArea
          name='summary'
          value={summary ? summary : ''}
          minRows={4.5}
          onChange={(e) => {
            handleInputChange(e, 'summary');
          }}
          allowClear={true}
          maxLength={500}
          hideLabel
          noMarginBottom
          showCount={false}
        />
        <p className='length-subtext'>{summary?.length}/500 characters</p>
      </SectionWarpper>
      <SectionWarpper>
        <p className='title'>{t('updateStory.form.writer')}</p>
        <DefaultInput
          title=''
          name='writer'
          value={writer ? writer : ''}
          onChange={(e) => {
            handleInputChange(e, 'writer');
          }}
          allowClear={true}
          noMarginBottom
          maxLength={30}
        />
        <p className='length-subtext'>
          {typeof writer?.length === 'number'
            ? `${writer?.length}/30 characters`
            : '0/30 characters'}
        </p>
      </SectionWarpper>
      <SectionWarpper>
        <p className='title'>{t('updateStory.form.originalName')}</p>
        <DefaultInput
          title=''
          name='original'
          value={original ? original : ''}
          onChange={(e) => {
            handleInputChange(e, 'original');
          }}
          allowClear={true}
          noMarginBottom
          maxLength={30}
        />
        <p className='length-subtext'>
          {typeof original?.length === 'number'
            ? `${original?.length}/30 characters`
            : '0/30 characters'}
        </p>
      </SectionWarpper>
      <SectionWarpper>
        <p className='title'>{t('updateStory.form.translator')}</p>
        <DefaultInput
          title=''
          name='translator'
          value={translator ? translator : ''}
          onChange={(e) => {
            handleInputChange(e, 'translator');
          }}
          allowClear={true}
          noMarginBottom
          maxLength={30}
        />
        <p className='length-subtext'>
          {typeof translator?.length === 'number'
            ? `${translator?.length}/30 characters`
            : '0/30 characters'}
        </p>
      </SectionWarpper>
      <SectionWarpper>
        <p className='title'>{t('updateStory.form.icon')}</p>
        <UploadStoryIcon
          iconImage={iconImage}
          setIconImage={setIconImage}
          iconKey={iconKey}
          setIconKey={setIconKey}
          iconBucket={iconBucket}
          setIconBucket={setIconBucket}
          lang={lang}
          project_id={listData?.project_id ? listData?.project_id : 0}
          setCancelButtonDisabled={setCancelButtonDisabled}
        />
      </SectionWarpper>
      <SubmitFlexBox style={{ columnGap: 12 }}>
        <>
          <PrimaryButton
            text={t('updateStory.form.buttonCancel')}
            height='40px'
            width='151px'
            bgColor='#FFFF'
            textColor={theme.colors.dark_400}
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.gray_6} `}
            rounded='6px'
            hoverBgColor={'#FFFF'}
            activeBgColor={'#FFFF'}
            selectedBgColor={'#FFFF'}
            onClick={handleCancel}
            disabled={isCancelButtonDisabled}
          />
          <PrimaryButton
            text={t('updateStory.form.buttonSubmit')}
            height='40px'
            width='151px'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={handleSubmit}
            disabled={isCancelButtonDisabled}
          />
        </>
      </SubmitFlexBox>
    </OtomeProjectContentWrapper>
  );
};

export default OtomeProjectContent;

const OtomeProjectContentWrapper = styled.div`
  & > * + * {
    margin-top: 16px;
  }
  .title,
  .length-subtext {
    font-size: ${theme.fontSizes.sm};
    font-style: normal;
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
  }
  .length-subtext {
    margin-top: 8px !important;
    font-size: ${theme.fontSizes.normal};
    font-style: normal;
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
    color: ${theme.colors.dark_200};
  }
  .ant-input-show-count-suffix {
    display: none;
  }
`;

const SectionWarpper = styled.div`
  & > * + * {
    margin-top: 8px;
  }
`;
